/**
 * FILE: src/layouts/notifications/index.jsx
 * 
 * Admin Notifications - Real-time notification list for admins
 * 
 * Features:
 * - Real-time SSE updates
 * - Mark as read / Mark all as read
 * - Filter by read/unread
 * - CASL permission-based display
 * - Links to notification templates management
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Grid,
  IconButton,
  ListItemText,
  Tabs,
  Tab,
  Badge,
  Tooltip,
  CircularProgress,
  List,
  ListItemButton,
  Avatar,
  Chip,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import DraftsIcon from "@mui/icons-material/Drafts";
import SendIcon from "@mui/icons-material/Send";
import SettingsIcon from "@mui/icons-material/Settings";

import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import KDButton from "components/KDButton";
import KDSnackbar from "components/KDSnackbar";
import DashboardNavbar from "components/DashboardNavbar";
import { useMaterialUIController } from "context";
import { useAbility, ACTIONS, SUBJECTS } from "context/AbilityContext";
import { useT } from "context/I18nContext";
import { useGlobalNotifications } from "context/NotificationContext";

// ============================================================================
// STYLES - Theme-aware
// ============================================================================

const getCardStyle = (darkMode) => ({
  background: darkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
  backdropFilter: "blur(10px)",
  border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
  borderRadius: "16px",
  overflow: "hidden",
});

const getStatCardStyle = (darkMode) => ({
  ...getCardStyle(darkMode),
  p: 2.5,
  display: "flex",
  alignItems: "center",
  gap: 2,
});

const getLabelStyle = (darkMode) => ({
  color: darkMode ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.5)",
  fontSize: "0.75rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

// Category icons
const categoryIcons = {
  system: InfoIcon,
  security: WarningIcon,
  success: CheckCircleIcon,
  error: ErrorIcon,
  info: InfoIcon,
  default: NotificationsIcon,
};

// Category colors
const categoryColors = {
  system: "#3B82F6",
  security: "#F59E0B",
  success: "#10B981",
  error: "#EF4444",
  info: "#6366F1",
  default: "#0D9488",
};

// ============================================================================
// STAT CARD COMPONENT
// ============================================================================

function StatCard({ icon: IconComponent, label, value, color, darkMode }) {
  return (
    <Card sx={getStatCardStyle(darkMode)}>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${color}40 0%, ${color}20 100%)`,
          borderRadius: "12px",
          p: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <IconComponent sx={{ color, fontSize: 28 }} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <KDTypography sx={getLabelStyle(darkMode)}>{label}</KDTypography>
        <KDTypography variant="h4" color={darkMode ? "white" : "dark"} fontWeight="bold">
          {value}
        </KDTypography>
      </Box>
    </Card>
  );
}

// ============================================================================
// NOTIFICATION ITEM COMPONENT
// ============================================================================

function NotificationItem({ notification, onMarkRead, onDelete, t, darkMode }) {
  const CategoryIcon = categoryIcons[notification.category] || categoryIcons.default;
  const categoryColor = categoryColors[notification.category] || categoryColors.default;
  
  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = typeof timestamp.toDate === "function" 
      ? timestamp.toDate() 
      : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return t("notifications.justNow");
    if (diff < 3600000) return t("notifications.minutesAgo", { count: Math.floor(diff / 60000) });
    if (diff < 86400000) return t("notifications.hoursAgo", { count: Math.floor(diff / 3600000) });
    return date.toLocaleDateString();
  };

  return (
    <ListItemButton
      sx={{
        borderRadius: "12px",
        mb: 1,
        backgroundColor: notification.read 
          ? "transparent" 
          : "rgba(13, 148, 136, 0.1)",
        "&:hover": {
          backgroundColor: notification.read 
            ? darkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.04)"
            : "rgba(13, 148, 136, 0.15)",
        },
        border: "1px solid",
        borderColor: notification.read 
          ? darkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.08)"
          : "rgba(13, 148, 136, 0.3)",
      }}
    >
      <Avatar
        sx={{
          mr: 2,
          bgcolor: `${categoryColor}30`,
          border: `1px solid ${categoryColor}40`,
        }}
      >
        <CategoryIcon sx={{ color: categoryColor, fontSize: 20 }} />
      </Avatar>
      
      <ListItemText
        primary={
          <KDBox display="flex" alignItems="center" gap={1}>
            <KDTypography 
              variant="subtitle2" 
              color={darkMode ? "white" : "dark"}
              fontWeight={notification.read ? "regular" : "bold"}
            >
              {notification.title}
            </KDTypography>
            {!notification.read && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#0D9488",
                }}
              />
            )}
          </KDBox>
        }
        secondary={
          <KDBox>
            <KDTypography variant="caption" color="text" sx={{ opacity: 0.8 }}>
              {notification.message}
            </KDTypography>
            <KDBox display="flex" alignItems="center" justifyContent="space-between" mt={0.5}>
              <KDTypography variant="caption" color="text" sx={{ opacity: 0.5 }}>
                {formatTime(notification.createdAt)}
              </KDTypography>
              <Chip
                label={notification.category || "notification"}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.65rem",
                  bgcolor: `${categoryColor}20`,
                  color: categoryColor,
                  border: `1px solid ${categoryColor}40`,
                }}
              />
            </KDBox>
          </KDBox>
        }
      />
      
      <KDBox display="flex" gap={0.5}>
        {!notification.read && (
          <Tooltip title={t("notifications.markRead")}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onMarkRead(notification.id);
              }}
              sx={{ color: darkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)" }}
            >
              <DraftsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </KDBox>
    </ListItemButton>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function Notifications() {
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const { miniSidenav, darkMode } = controller;
  const ability = useAbility();
  const t = useT();
  
  // Use the SSE-based notifications hook
  const {
    notifications,
    unreadCount,
    loading,
    error,
    connected,
    markAsRead,
    refresh,
  } = useGlobalNotifications();
  
  // UI State
  const [filter, setFilter] = useState("all"); // "all" | "unread"
  const [snackbar, setSnackbar] = useState({ open: false, color: "info", title: "", content: "" });
  
  // Permissions
  const canManageTemplates = ability.can(ACTIONS.READ, SUBJECTS.NOTIFICATION_TEMPLATE);
  
  const showSnackbar = (color, title, content = "") => {
    setSnackbar({ open: true, color, title, content });
  };
  
  // Filter notifications
  const filteredNotifications = filter === "unread" 
    ? notifications.filter(n => !n.read)
    : notifications;
  
  // Handle mark as read
  const handleMarkRead = async (id) => {
    try {
      await markAsRead(id);
    } catch (err) {
      showSnackbar("error", t("common.error"), err.message);
    }
  };
  
  // Handle mark all as read
  const handleMarkAllRead = async () => {
    try {
      for (const n of notifications.filter(n => !n.read)) {
        await markAsRead(n.id);
      }
      showSnackbar("success", t("common.done"), t("notifications.allMarkedRead"));
    } catch (err) {
      showSnackbar("error", t("common.error"), err.message);
    }
  };
  
  // Stats
  const totalCount = notifications.length;
  const readCount = notifications.filter(n => n.read).length;
  
  // Theme-aware colors
  const textColor = darkMode ? "white" : "dark";
  const secondaryColor = darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)";
  const cardStyle = getCardStyle(darkMode);
  
  return (
    <KDBox
      sx={({ breakpoints, transitions }) => ({
        p: 3,
        marginLeft: miniSidenav ? "80px" : "260px",
        transition: transitions.create("margin-left", {
          easing: transitions.easing.easeInOut,
          duration: transitions.duration.standard,
        }),
        [breakpoints.down("xl")]: { marginLeft: 0 },
      })}
    >
      <DashboardNavbar />
      
      <KDBox mt={4}>
        {/* Header */}
        <KDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <KDBox>
            <KDTypography variant="h4" color={textColor} fontWeight="bold">
              {t("notifications.title")}
            </KDTypography>
            <KDTypography variant="body2" color="text" component="div" sx={{ opacity: 0.7 }}>
              {connected ? (
                <KDBox display="flex" alignItems="center" gap={0.5}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#10B981" }} />
                  {t("notifications.realTimeConnected")}
                </KDBox>
              ) : (
                <KDBox display="flex" alignItems="center" gap={0.5}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#F59E0B" }} />
                  {t("notifications.pollingMode")}
                </KDBox>
              )}
            </KDTypography>
          </KDBox>
          
          <KDBox display="flex" gap={1}>
            <Tooltip title={t("notifications.refresh")}>
              <IconButton onClick={refresh} sx={{ color: secondaryColor }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            
            {unreadCount > 0 && (
              <KDButton
                variant="outlined"
                color="info"
                size="small"
                startIcon={<MarkEmailReadIcon />}
                onClick={handleMarkAllRead}
              >
                {t("notifications.markAllRead")}
              </KDButton>
            )}
            
            {canManageTemplates && (
              <KDButton
                variant="gradient"
                color="info"
                size="small"
                startIcon={<SettingsIcon />}
                onClick={() => navigate("/admin/notification-templates")}
              >
                {t("notifications.manageTemplates")}
              </KDButton>
            )}
          </KDBox>
        </KDBox>
        
        {/* Stats Cards */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={NotificationsIcon}
              label={t("notifications.total")}
              value={totalCount}
              color="#0D9488"
              darkMode={darkMode}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={NotificationsActiveIcon}
              label={t("notifications.unreadTab")}
              value={unreadCount}
              color="#F59E0B"
              darkMode={darkMode}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={DraftsIcon}
              label={t("notifications.read")}
              value={readCount}
              color="#10B981"
              darkMode={darkMode}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={SendIcon}
              label={t("notifications.sentToday")}
              value="—"
              color="#6366F1"
              darkMode={darkMode}
            />
          </Grid>
        </Grid>
        
        {/* Filter Tabs */}
        <Card sx={{ ...cardStyle, mb: 3, p: 2 }}>
          <Tabs
            value={filter}
            onChange={(e, v) => setFilter(v)}
            sx={{
              minHeight: 36,
              "& .MuiTab-root": {
                color: darkMode ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.6)",
                minHeight: 36,
                py: 0,
              },
              "& .Mui-selected": { color: "#0D9488" },
              "& .MuiTabs-indicator": { bgcolor: "#0D9488" },
            }}
          >
            <Tab label={t("notifications.allTab", { count: totalCount })} value="all" />
            <Tab
              label={
                <Badge badgeContent={unreadCount} color="error" max={99}>
                  {t("notifications.unreadTab")}
                </Badge>
              }
              value="unread"
            />
          </Tabs>
        </Card>
        
        {/* Notifications List */}
        <Card sx={{ ...cardStyle, p: 2 }}>
          {loading ? (
            <KDBox display="flex" justifyContent="center" py={6}>
              <CircularProgress sx={{ color: "#0D9488" }} />
            </KDBox>
          ) : error ? (
            <KDBox textAlign="center" py={6}>
              <ErrorIcon sx={{ fontSize: 48, color: "#EF4444", mb: 2 }} />
              <KDTypography color={textColor}>{t("notifications.errorLoading")}</KDTypography>
              <KDTypography variant="caption" color="text">{error}</KDTypography>
            </KDBox>
          ) : filteredNotifications.length === 0 ? (
            <KDBox textAlign="center" py={6}>
              <NotificationsIcon sx={{ fontSize: 48, color: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)", mb: 2 }} />
              <KDTypography color={textColor}>
                {filter === "unread" ? t("notifications.noUnread") : t("notifications.noNotifications")}
              </KDTypography>
            </KDBox>
          ) : (
            <List disablePadding>
              {filteredNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkRead={handleMarkRead}
                  t={t}
                  darkMode={darkMode}
                />
              ))}
            </List>
          )}
        </Card>
      </KDBox>
      
      {/* Snackbar */}
      <KDSnackbar
        color={snackbar.color}
        icon="check"
        title={snackbar.title}
        content={snackbar.content}
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        close={() => setSnackbar({ ...snackbar, open: false })}
        bgWhite
      />
    </KDBox>
  );
}
